import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Active, closestCenter, DndContext, Over } from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext } from '@dnd-kit/sortable'
import clsx from 'clsx'

import { ActionFlag } from '@makyoapp/makyo-common'
import { Download16, OpenNew16, Plus16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant, Divider, MoreDropdown, SimpleInput } from '@makyoapp/makyo-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useAppOverlaysContext } from '@/context/AppOverlaysContext'
import { useSortableHandle } from '@/hooks/useSortableHandle'
import { BuilderOverlay } from '@/shared/overlay'
import { SortableItem } from '@/components/SortableItem'

import { SearchField } from './SearchField'

/**
 * new makyo overlay
 */
export default function OverlayPanelNormal() {
  const { overlays, addOverlay, updateOverlayList } = useAppOverlaysContext()

  const [simpleEdit, setSimpleEdit] = useState('')
  const [loading, setLoading] = useState(false)
  const [searchData, setSearchData] = useState('')

  // Memoize function untuk extract text dari title (jika overlay title juga JSON)
  const extractTextFromTitle = useCallback((titleJson: string | null | undefined): string => {
    if (!titleJson || typeof titleJson !== 'string') return titleJson || ''

    try {
      const parsed = JSON.parse(titleJson)
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed[0]?.children?.[0]?.text || ''
      }
      return ''
    } catch (error) {
      console.warn('Failed to parse overlay title JSON:', error)
      return titleJson
    }
  }, [])

  // Memoize filtered overlays untuk performa yang lebih baik
  const filteredOverlays = useMemo(() => {
    if (!overlays) return []

    if (!searchData.trim()) return overlays

    const searchTerm = searchData.toLowerCase().trim()

    return overlays.filter((overlay) => {
      // Safety check untuk overlay properties
      if (!overlay) return false

      const titleText = extractTextFromTitle(overlay.title).toLowerCase()
      const slugText = (overlay.slug || '').toLowerCase()

      return titleText.includes(searchTerm) || slugText.includes(searchTerm)
    })
  }, [overlays, searchData, extractTextFromTitle])

  // Memoize drag end callback untuk mencegah re-creation
  const dragEndCb = useCallback(
    async (active: Active, over: Over) => {
      if (!active.id || !over.id) return

      await updateOverlayList((currentOverlays) => {
        const oldIndex = currentOverlays.findIndex((p) => p.id === active.id)
        const newIndex = currentOverlays.findIndex((p) => p.id === over.id)

        if (oldIndex === -1 || newIndex === -1) return currentOverlays

        return arrayMove(currentOverlays, oldIndex, newIndex)
      })
    },
    [updateOverlayList]
  )

  const { sensors, handleDragEnd } = useSortableHandle(dragEndCb)

  const handleAddOverlay = async () => {
    setLoading(true)
    try {
      await addOverlay()
    } catch (error) {
      console.error('Failed to add overlay:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='p-4'>
      <div className={clsx('page-list flex flex-col pb-4')}>
        <div className='uppercase py-2'>
          <Trans>Overlays</Trans>
        </div>
        {overlays?.length > 0 && <SearchField search={searchData} setSearch={setSearchData} className='-mx-4' />}
        <div className='flex flex-col gap-1'>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToFirstScrollableAncestor]}
          >
            <SortableContext items={filteredOverlays}>
              {filteredOverlays.map((overlay, index) => {
                if (!overlay || overlay.tableId) return null

                return (
                  <SortableOverlay
                    isEdit={simpleEdit === overlay.id}
                    overlay={overlay}
                    key={overlay.id}
                    index={index}
                    onFinishEdit={() => {
                      setSimpleEdit('')
                    }}
                  />
                )
              })}
            </SortableContext>
          </DndContext>
        </div>
      </div>
      <Button disabled={loading} icon={loading ? undefined : <Plus16 />} onClick={handleAddOverlay} className='w-full'>
        {loading ? <span className='loading spin' /> : <Trans>New Overlay</Trans>}
      </Button>
      <Divider />
    </div>
  )
}

function SortableOverlay({
  overlay,
  isEdit,
  onFinishEdit,
  index,
}: {
  overlay: BuilderOverlay
  isEdit: boolean
  onFinishEdit?: () => void
  index: number
}) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)

  const [title, setTitle] = useState(overlay.title)

  const { setPageAction, exportToAiJSON } = useAppBuilderContext()
  const { activeLayer } = useActiveLayerContext()

  const {
    showOverlay,
    activeOverlays,
    updateOverlay,
    deleteOverlay,
    closeOverlay,
    setActiveOverlayId,
    setOverlayAction,
  } = useAppOverlaysContext()

  const isActive = !!(activeOverlays.findIndex((o) => o?.id === overlay?.id) > -1)

  const handleTogglePreview = () => {
    if (isActive) {
      closeOverlay(overlay.id)
    } else {
      showOverlay(overlay.id)
    }
  }

  useEffect(() => {
    if (isEdit) {
      inputRef.current?.focus()
    }
  }, [isEdit, inputRef])

  const pageTitle = overlay.title

  return (
    <SortableItem
      className={clsx(
        !overlay.enabled && 'opacity-50',
        activeLayer.id === overlay.id && 'bg-zinc-200 dark:bg-slate-700'
      )}
      onClick={() => {
        if (!isActive) {
          showOverlay(overlay.id)
        }
        setPageAction({
          flag: ActionFlag.Edit,
          page: overlay.id,
        })
      }}
      id={overlay.id}
      title={
        isEdit ? (
          <input
            className='bg-transparent border bdr w-full'
            ref={inputRef}
            id={`overlat-title-edit-${overlay.id}`}
            value={title}
            onFocus={(e) => {
              e.currentTarget.setSelectionRange(0, title.length)
            }}
            onChange={(e) => {
              setTitle(e.currentTarget.value)
            }}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                inputRef.current?.blur()
              }
            }}
            onBlur={async () => {
              if (isEdit && title) {
                await updateOverlay(index, (prevPage) => {
                  prevPage.title = title
                })
                onFinishEdit?.()
              }
            }}
          />
        ) : (
          <div className={clsx('txt')}>{pageTitle}</div>
        )
      }
      icon={<MaterialIcon size={20} icon={overlay.icon} />}
      wrapTitle={false}
      reorderTitle={t('Reorder Screen')}
      onEdit={() => {
        if (!isActive) {
          showOverlay(overlay.id)
        }
        setActiveOverlayId(overlay.id)
        setOverlayAction({
          flag: ActionFlag.Edit,
          page: overlay.id,
        })
      }}
      editTitle={t('Edit Screen')}
      onDelete={() => {
        deleteOverlay(overlay.id)
      }}
      deleteTitle={t('Delete Screen')}
      deleteAtEnd={false}
    >
      {/* Toggle Preview Overlay */}
      <Button
        variant={ButtonVariant.Ghost}
        icon={<OpenNew16 width={16} height={16} className={clsx(isActive && 'text-primary-500')} />}
        className='hover:text-primary-500!'
        noPadding
        squared
        onClick={handleTogglePreview}
        title={isActive ? t('Hide Overlay') : t('Show Overlay')}
        noTip
      />

      <MoreDropdown menuClassName='z-10 text-m-m' portalMenu placement='left' menuBtnClassName='w-9 h-9 flex-cc'>
        <MoreDropdown.MenuItem
          onClick={async () => {
            await updateOverlay(index, (prevPage) => {
              prevPage.enabled = !prevPage.enabled
            })
          }}
        >
          {overlay.enabled ? (
            <MaterialIcon size={20} icon='unpublished' />
          ) : (
            <MaterialIcon size={20} icon='check_circle' />
          )}
          <span>{overlay.enabled ? <Trans>Disable this screen</Trans> : <Trans>Enable this screen</Trans>}</span>
        </MoreDropdown.MenuItem>

        <MoreDropdown.MenuItem
          onClick={async () => {
            await exportToAiJSON('overlay', { id: overlay.id })
          }}
        >
          <Download16 />
          <span>
            <Trans>Export to AI</Trans>
          </span>
        </MoreDropdown.MenuItem>

        {/* // TODO: add clone support for overlay */}
        {/* 
        {!Boolean(overlay.tableId) && (
          <MoreDropdown.MenuItem
            onClick={async () => {
              //   await clonePage(page)
            }}
          >
            <Copy16 />
            <span>
              <Trans>Clone</Trans>
            </span>
          </MoreDropdown.MenuItem>
        )} */}
      </MoreDropdown>
    </SortableItem>
  )
}
